import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal } from 'antd';
import { ModalProps } from 'antd/lib/modal/Modal';
import Column from 'antd/lib/table/Column';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { NumberedTable } from 'common/components/NumberedTable';
import { useEnsureContext } from 'common/hooks';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { purge } from 'lib/ramda/purge';
import { MedicalOperator, MedicalOperatorDTO } from 'models/medicalOperator';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { UserStatus } from 'models/userStatus';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { MedicalOperatorsFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/MedicalOperatorsFilters';
import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from 'react-query';
import styled from 'styled-components';
import { PartnerDetailsDTO } from 'models/higoDevice';
import { useRole } from 'common/hooks/useRole';
import { isNil } from 'ramda';

interface BusinessPartnerMedicalOperatorTransferPatientModalProps
  extends ModalProps {
  medicalOperatorId: number;
  hideModal: () => void;
  patientsIds: number[];
  onTransfered: () => void;
  medicalOperator: MedicalOperator;
  partnerHardcoded: PartnerDetailsDTO | undefined;
}

const StyledModal = styled(Modal)`
  top: 25px;
  width: 90vw !important;
import { UserStatus } from 'models/userStatus';

  .ant-modal-body {
    height: 80vh;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

export const BusinessPartnerMedicalOperatorTransferPatientModal: React.FC<BusinessPartnerMedicalOperatorTransferPatientModalProps> = ({
  medicalOperatorId,
  medicalOperator,
  partnerHardcoded,
  hideModal,
  patientsIds,
  onTransfered,
  ...modalProps
}) => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const intlMessage = useFormatMessage();

  const [selectedMedicalOperatorId, setSelectedMedicalOperatorId] = useState<
    number
  >();

  const [mutate, mutationHelpers] = useMutation(
    BusinessPartnersAPI.transferPatientsToMedicalOperator
  );

  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState({
    partnerName: partnerHardcoded?.name,
  });
  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const { isFetching, data } = useQuery(
    [QueryKey.BusinessPartnerMedicalOperatorsList, pageMetaData, filters],
    () =>
      BusinessPartnersAPI.fetchMedicalOperators(
        partnerHardcoded && partnerHardcoded?.id !== 0
          ? partnerHardcoded?.id
          : bpContext.id,
        {
          ...pageMetaData,
          ...filters,
        }
      ),
    {
      keepPreviousData: true,
    }
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const clearSelectedAndHide = () => {
    setSelectedMedicalOperatorId(undefined);
    hideModal();
  };

  const transferPatients = () => {
    Modal.confirm({
      title: intlMessage({
        id: 'medicalOperators.transferPatients.confirmation',
      }),
      icon: <ExclamationCircleOutlined />,
      okText: intlMessage({ id: 'yes' }),
      cancelText: intlMessage({ id: 'cancel' }),
      okType: 'danger',
      onOk: () =>
        mutate(
          {
            businessPartnerId: medicalOperator?.partnerDetails
              ? medicalOperator.partnerDetails.id
              : bpContext.id,
            dto: {
              patientIds: patientsIds,
              medicalOperatorId,
              assignMedicalOperatorId: selectedMedicalOperatorId!,
            },
          },
          {
            onSuccess: () => {
              message.success(
                intlMessage({ id: 'medicalOperators.transferPatients.success' })
              );
            },
          }
        ).then(() => {
          onTransfered();
          clearSelectedAndHide();
        }),
    });
  };

  const dataToShow = useMemo(
    () =>
      data?.content.filter((item) => item?.userId !== medicalOperator.userId),
    [data, medicalOperator.userId]
  );

  const shouldShowPagination = data?.content && data?.totalElements > 20;

  const footer = [
    <Button key="cancel" onClick={clearSelectedAndHide}>
      <FormattedMessage id="cancel" />
    </Button>,
    <Button
      key="transfer"
      type="primary"
      disabled={!selectedMedicalOperatorId}
      loading={mutationHelpers.isLoading}
      onClick={() => transferPatients()}
    >
      <FormattedMessage
        id="medicalOperators.transferPatients"
        tagName={'span'}
      />
    </Button>,
  ];

  return (
    <StyledModal
      title={
        intlMessage({
          id: 'medicalOperators.transferPatientsToMedicalOperator',
        }) + ':'
      }
      footer={footer}
      onCancel={hideModal}
      {...modalProps}
    >
      <>
        <MedicalOperatorsFilters
          shouldHidePartnerColumn
          onSubmit={(values) => setFilters(purge(values))}
          partnerHardcoded={partnerHardcoded?.name}
        />
        <NumberedTable
          rowKey="userId"
          loading={isFetching}
          dataSource={dataToShow}
          pagination={shouldShowPagination ? paginationConfig : false}
          scroll={{ x: 'auto' }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedMedicalOperatorId
              ? [selectedMedicalOperatorId]
              : [],
            onSelect: (record) => setSelectedMedicalOperatorId(record.userId),
            getCheckboxProps: (record: MedicalOperatorDTO) => ({
              disabled:
                record.userId === medicalOperatorId ||
                record.status === UserStatus.Archived,
            }),
          }}
          onRow={(record) => ({
            onClick: () => {
              if (
                record.userId !== medicalOperatorId &&
                record.status !== UserStatus.Archived
              ) {
                setSelectedMedicalOperatorId(record.userId);
              }
            },
          })}
          rowClassName="ant-table-row-interactive"
          style={{ marginTop: '1rem' }}
        >
          <Column
            title={intlMessage({ id: 'name' })}
            dataIndex="firstName"
            render={(value, record: MedicalOperatorDTO) =>
              record.firstName + ' ' + record.lastName
            }
          />
          <Column title="Email" dataIndex="email" />
          {!shouldHidePartnerColumn && (
            <Column
              title={intlMessage({ id: 'partner' })}
              dataIndex={['partnerDetails', 'name']}
            />
          )}
        </NumberedTable>
      </>
    </StyledModal>
  );
};
